import { GenericObject } from './common';

import { InsulationAreaSetting, InsuEnum } from './entity';
import { SymbolType, SPECIAL_JOB_ITEM } from './options';
import { BPSymbol, InsulationSymbol } from './symbols';

/**
 * @deprecated in favor of backend item definition(just keep it as reference)
 *
 * Entity types eligible to have insulation applied.
 * Used to gate both the per-symbol insulation button and
 * the polygon-zone "Apply" action.
 * @2026/06/03
 */
export const INSULATION_ELIGIBLE_TYPES = new Set([
  'CENTER_LINE_REDUCER',
  'ECCENTRIC_REDUCER',
  'ELBOW_HVAC',
  'EXHAUST_DUCT',
  'RETURN_DUCT',
  'RETURN_DUCT_DOWN',
  'ROUND_DUCT_UP',
  'SPIN_IN',
  'SIDE_TAKEOFF', // ?
  'T_JUNCTION_DUCT',
  'Y_JUNCTION',
  'DUCT_LENGTH',
  'PIPE_LENGTH', // @2026/06/18
  /** == @2026/06/23 for pipe items == */
  'ACV',
  'BALL_VALVE',
  'BUTTERFLY_VALVE',
  'CBV',
  'ELBOW_RECTANGULAR',
  'ELBOW_PIPING',
  'GATE_VALVE',
  'PRV',
  'PUMP',
  'SOLENOID_VALVE',
  'SOLENOIDAL_VALVE',
  'SRV',
  'STRAINER',
  'UNION',
  'V_AND_C',
  /** == @2026/06/23 for plumbing items == */
  'BALANCING_VALVE',
  'MIXING_VALVE',
  'TEMPERATURE_AND_PRESSURE_RELIEF_VALVE',
]);

/**
 * Insulation painting brush settings, chosen in the `Insulation Painting Settings`
 * modal before painting on the drawing canvas - @2026/08/14
 */
export type InsulationBrushSetting = {
  /** insulation serve category, only symbols of this category get painted */
  serveCategory: 'DUCT' | 'PIPE';
  exposeType: string;
  insulationType: string;
  insulationThickness: string;
  claddingType: string;
};

export const DEFAULT_INSULATION: InsulationAreaSetting = {
  areaName: 'Default',
  areaMode: 'default',
  serveCategory: 'DUCT',
  exposeType: 'exposed',
};

/**
 * Map insulation setting field to backend field
 * @param prop insulation field
 * @returns backend field
 */
export const getInsulationToBackendField = (prop: string) => {
  const insulationFieldMapping: { [key: string]: string } = {
    ['areaName']: InsuEnum.AN,
    ['areaMode']: InsuEnum.AT,
    ['serveCategory']: InsuEnum.SC,
    ['insulationType']: InsuEnum.IT,
    ['insulationThickness']: InsuEnum.IC,
    ['claddingType']: InsuEnum.CT,
    ['exposeType']: InsuEnum.IX,
  };
  return insulationFieldMapping[prop];
};

/**
 * Transform those insulation properties from polygon setting into backend needed properties
 *
 * @param insltObj insulation setting object
 * @returns detail for insulation symbol detail
 */
export const generatePolygonProps = (
  insltObj: InsulationAreaSetting,
): GenericObject => {
  const insltMapping: GenericObject = {
    [InsuEnum.AN]: insltObj.areaName,
    [InsuEnum.AT]: insltObj.areaMode,
    [InsuEnum.SC]: insltObj.serveCategory,
    [InsuEnum.IX]: insltObj.exposeType,
  };
  return insltMapping;
};

/**
 * insulation group setting for each system, and `InsulationSubPanel`
 * @param insltObj
 * @returns
 */
export const generateInsulationProps = (
  insltObj: InsulationAreaSetting,
): GenericObject => {
  return {
    [InsuEnum.SC]: insltObj.serveCategory,
    [InsuEnum.IX]: insltObj.exposeType,
    [InsuEnum.IT]: insltObj.insulationType,
    [InsuEnum.IC]: insltObj.insulationThickness,
    [InsuEnum.CT]: insltObj.claddingType,
  };
};

/**
 * Show insulation area in side panel:
 * - first check newly created polygon object;
 * - then check saved polygon object;
 *
 * Transform backend saved insulation properties into insulation setting props
 * @param detail general object
 * @returns insulation setting
 */
export const generateInsulationSetting = (
  detail: GenericObject,
  isPolygon: boolean,
): InsulationAreaSetting => {
  const insltObj: InsulationAreaSetting = {
    areaMode: detail['areaMode'] || detail[InsuEnum.AT] || 'default',
    areaName: detail['areaName'] || detail[InsuEnum.AN] || '',
    serveCategory: detail['serveCategory'] || detail[InsuEnum.SC] || 'DUCT',
    exposeType: detail['exposeType'] || detail[InsuEnum.IX] || 'exposed',
  };
  if (isPolygon) {
    insltObj.property_groups = detail['property_groups'] || {};
    return insltObj;
  }
  // indicate the insulation length, could be partial length of original pipe
  // @2026/04/20
  if (!isPolygon) {
    insltObj.segment = {
      length_1: detail['length_1'],
      length_unit_str: detail['length_unit_str'],
    };
    // normal symbol
    insltObj.insulationType = detail['insulationType'] || detail[InsuEnum.IT];
    insltObj.insulationThickness =
      detail['insulationThickness'] || detail[InsuEnum.IC];
    insltObj.claddingType = detail['claddingType'] || detail[InsuEnum.CT];
  }
  return insltObj;
};

/**
 * Compose insulation properties to remove from a job item:
 *
 * - i_expose_type
 * - i_insulation_type
 * - i_thickness
 * - i_cladding_type
 * - i_serve_category
 *
 * @param detail
 * @returns
 */
export const getInsulationFieldPairs = () => {
  const fields: { name: string }[] = [];
  fields.push({ name: InsuEnum.IX });
  fields.push({ name: InsuEnum.IT });
  fields.push({ name: InsuEnum.IC });
  fields.push({ name: InsuEnum.CT });
  fields.push({ name: InsuEnum.SC });
  return fields;
};

export const genInsulationFieldPairs = (detail: GenericObject) => {
  const fields: { name: string; value: string | number }[] = [];
  fields.push({ name: InsuEnum.IX, value: detail[InsuEnum.IX] });
  fields.push({ name: InsuEnum.IT, value: detail[InsuEnum.IT] });
  fields.push({ name: InsuEnum.IC, value: detail[InsuEnum.IC] });
  fields.push({ name: InsuEnum.CT, value: detail[InsuEnum.CT] });
  fields.push({ name: InsuEnum.SC, value: detail[InsuEnum.SC] });
  return fields;
};

export function isInsulationSymbol(smb: BPSymbol): smb is InsulationSymbol {
  const isVirtSymbol = smb.type === SymbolType.VIRT;
  const isInsulation = smb.class === SPECIAL_JOB_ITEM.INSLT;
  return isVirtSymbol && isInsulation;
}

// ─── UI option lists ──────────────────────────────────────────────────────────

export type AreaType = 'polygon' | 'default';

export const SERV_CATEGORY_OPTIONS = [
  { value: 'DUCT', label: 'DUCT' },
  { value: 'PIPE', label: 'PIPE' },
];

export const INSULATION_AREA_OPTIONS = [
  // none-insulation area
  { label: 'None', value: 'none' },
  { label: 'Exposed', value: 'exposed' },
  { label: 'Concealed', value: 'concealed' },
  { label: 'Outdoors', value: 'outdoors' },
  { label: 'Mech room', value: 'mech_room' },
  { label: 'Exposed Indoors', value: 'exposed_indoors' },
];

/**
 * materials
 */
export const INSULATION_TYPE_OPTIONS = {
  DUCT: [
    { value: 'none', label: 'None' },
    { value: 'duct_wrap', label: 'Duct wrap' },
    { value: 'rigid_duct_board', label: 'Rigid duct board' },
    { value: 'mineral_wool_blanket', label: 'Mineral wool blanket' },
    { value: 'elastomeric', label: 'Elastomeric' },
    { value: 'foam_board', label: 'Foam board' },
    { value: 'rubber', label: 'Rubber' },
  ],
  PIPE: [
    { value: 'none', label: 'None' },
    { value: 'fiberglass', label: 'Fiberglass' },
    { value: 'mineral_wool', label: 'Mineral wool' },
    { value: 'elastomeric_rubber', label: 'Elastomeric rubber' },
    { value: 'closed_cell_foam', label: 'Closed-cell foam' },
    { value: 'cellular_glass', label: 'Cellular glass' },
    { value: 'foam', label: 'Foam' },
  ],
};

export const INSULATION_THICKNESS_OPTIONS = {
  DUCT: [
    { value: '1/2"', label: '1/2"' },
    { value: '1"', label: '1"' },
    { value: '1-1/2"', label: '1-1/2"' },
    { value: '2"', label: '2"' },
    { value: '3"', label: '3"' },
    { value: '4"', label: '4"' },
    { value: '5"', label: '5"' },
  ],
  PIPE: [
    { value: '3/8"', label: '3/8"' },
    { value: '1/2"', label: '1/2"' },
    { value: '3/4"', label: '3/4"' },
    { value: '1"', label: '1"' },
    { value: '1-1/2"', label: '1-1/2"' },
    { value: '2"', label: '2"' },
    { value: '2-1/2"', label: '2-1/2"' },
    { value: '3"', label: '3"' },
    { value: '4"', label: '4"' },
    { value: '5"', label: '5"' },
  ],
};

export const CLADDING_TYPE_OPTIONS = {
  DUCT: [
    { value: 'none', label: 'None' },
    { value: 'fsk', label: 'FSK' },
    { value: 'pvc', label: 'PVC' },
    { value: 'aluminum', label: 'Aluminum' },
    { value: 'galvanized', label: 'Galvanized' },
    { value: 'stainless', label: 'Stainless' },
    { value: 'weatherproof', label: 'Weatherproof' },
  ],
  PIPE: [
    { value: 'none', label: 'None' },
    { value: 'pvc', label: 'PVC' },
    { value: 'aluminum', label: 'Aluminum' },
    { value: 'galvanized', label: 'Galvanized' },
    { value: 'stainless', label: 'Stainless' },
    { value: 'canvas', label: 'Canvas' },
    { value: 'weatherproof', label: 'Weatherproof' },
  ],
};
